import React, { useCallback, useId, useState } from 'react';
import { Box, Popover } from '@mui/material';
import { DarkModeOutlined as DarkModeOutlinedIcon, WbSunny as WbSunnyIcon } from '@mui/icons-material';
import { FigmaGlyph } from '../bot-constructor/FigmaGlyph';
import { useTranslation } from 'react-i18next';
import { useThemeId } from '../../theme/ThemeContext';

const iconSx = { fontSize: 20, flexShrink: 0 } as const;

/** Луна с диагональной перечёркиванием (пара «солнце» в светлой теме). */
function CrossedMoonIcon() {
  return (
    <Box
      sx={{
        position: 'relative',
        width: 22,
        height: 22,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'currentColor',
        flexShrink: 0,
      }}
    >
      <DarkModeOutlinedIcon sx={{ fontSize: iconSx.fontSize }} />
      <Box
        component="span"
        aria-hidden
        sx={{
          position: 'absolute',
          width: '78%',
          height: 2,
          borderRadius: 1,
          bgcolor: 'currentColor',
          transform: 'rotate(-38deg)',
          opacity: 0.9,
        }}
      />
    </Box>
  );
}

type ThemeChoice = 'smsby' | 'dataedge';

const THEME_ORDER: ThemeChoice[] = ['smsby', 'dataedge'];

const ThemeSelector: React.FC<{ inverted?: boolean; variant?: 'default' | 'editor' }> = ({
  inverted = false,
  variant = 'default',
}) => {
  const { themeId, setThemeId } = useThemeId();
  const { t } = useTranslation();
  const listId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const menuBg = inverted ? '#000000' : '#eceff4';
  const optBorder = inverted ? '#fff' : 'rgba(13,13,13,0.35)';
  const optBorderMuted = inverted ? 'rgba(255,255,255,0.45)' : 'rgba(13,13,13,0.22)';
  const currentLabel = themeId === 'smsby' ? t('common.themeLight') : t('common.themeDark');

  const close = useCallback(() => setAnchorEl(null), []);
  const pick = useCallback((id: ThemeChoice) => {
    setThemeId(id);
    close();
  }, [setThemeId, close]);

  return (
    <>
      <Box
        component="button"
        type="button"
        id="theme-selector-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={currentLabel}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: variant === 'editor' ? '5px' : 0,
          minWidth: variant === 'editor' ? 56 : 40,
          height: variant === 'editor' ? 35 : 40,
          px: variant === 'editor' ? '4px' : 1,
          py: variant === 'editor' ? 0 : 0.5,
          boxSizing: 'border-box',
          borderRadius: variant === 'editor' ? '7px' : '50%',
          cursor: 'pointer',
          m: 0,
          color: variant === 'editor' ? '#ffb300' : inverted ? '#fff' : 'inherit',
          bgcolor: 'transparent',
          border: variant === 'editor' ? '1px solid #d1d1d1' : 'none',
          '&:hover': { bgcolor: inverted ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)' },
          '&:focus-visible': {
            outline: inverted ? '2px solid rgba(255,255,255,0.85)' : '2px solid rgba(25,118,210,0.6)',
            outlineOffset: 2,
          },
        }}
      >
        {variant === 'editor' ? (
          <FigmaGlyph src="/images/editor/icon-sun.svg" width={24} height={24} />
        ) : themeId === 'smsby' ? (
          <WbSunnyIcon sx={iconSx} />
        ) : (
          <DarkModeOutlinedIcon sx={iconSx} />
        )}
        {variant === 'editor' && (
          <FigmaGlyph src="/images/editor/icon-chevron.svg" width={18} height={18} transform="rotate(-90deg)" />
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        marginThreshold={8}
        slotProps={{
          paper: {
            elevation: 6,
            sx: {
              borderRadius: 1,
              overflow: 'hidden',
              border: inverted ? '1px solid rgba(255,255,255,0.35)' : '1px solid rgba(0,0,0,0.12)',
              mt: 0.5,
              bgcolor: menuBg,
            },
          },
        }}
      >
        <Box
          id={listId}
          role="listbox"
          aria-labelledby="theme-selector-trigger"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            py: 1,
            px: 1,
            minWidth: 52,
            boxSizing: 'border-box',
          }}
        >
          {THEME_ORDER.map((id) => {
            const selected = id === themeId;
            const label = id === 'smsby' ? t('common.themeLight') : t('common.themeDark');
            return (
              <Box
                key={id}
                component="button"
                type="button"
                role="option"
                aria-selected={selected}
                aria-label={label}
                onClick={() => pick(id)}
                sx={{
                  width: 34,
                  height: 34,
                  minWidth: 34,
                  p: 0,
                  m: 0,
                  border: `2px solid ${selected ? optBorder : optBorderMuted}`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  bgcolor: 'transparent',
                  color: inverted ? '#fff' : 'inherit',
                  opacity: selected ? 1 : inverted ? 0.55 : 0.65,
                  transition: 'opacity 0.15s ease, transform 0.15s ease',
                  '&:hover': { opacity: 1, transform: 'scale(1.06)' },
                  '&:focus-visible': {
                    outline: inverted ? '2px solid rgba(255,255,255,0.85)' : '2px solid rgba(25,118,210,0.6)',
                    outlineOffset: 2,
                  },
                }}
              >
                {id === 'smsby' ? <WbSunnyIcon sx={{ fontSize: 18 }} /> : <DarkModeOutlinedIcon sx={{ fontSize: 18 }} />}
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};

export default ThemeSelector;
